import ColumnChart from "@/shared/components/common/columnCharts";
import RadialBarChart from "@/shared/components/common/radialBar";
import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import moment from "jalali-moment";
import { numberSeparator, toPersianNumber, } from "@/hooks/useThousandsSeparator";
import { useNavigate } from "react-router";

interface ContentChartProps {
  listIncreaseGiftChartBar: any;
  activeTab: string;
  setActiveTab: any;
  listIncreaseGiftChart: any;
  handleOpenModal: any
}
/**
 * ContentChart
 *
 * نمایش نمودارهای تراکنش هدیه و وضعیت موجودی مصرف شده و باقیمانده.
 * کامپوننت شامل:
 * - نمودار ستونی تراکنش‌های هدیه
 * - نمودارهای دایره‌ای موجودی هدیه و هدیه‌های اهدا شده
 * - نمایش بیشترین تراکنش‌ها
 * - دکمه‌های افزایش موجودی و هدیه کاربران
 *
 * این نسخه به‌صورت واکنش‌گرا طراحی شده و برای نمایش در موبایل، تبلت و دسکتاپ مناسب است.
 *
 * @param listIncreaseGiftChartBar - داده‌های نمودار دایره‌ای موجودی
 * @param activeTab - تب فعال نمودار (روزانه، هفتگی، ماهانه)
 * @param setActiveTab - تابع برای تغییر تب فعال
 * @param listIncreaseGiftChart - داده‌های نمودار ستونی
 * @param handleOpenModal - تابع باز کردن مودال افزایش موجودی
 * @returns JSX.Element
 */

const ContentChart = ({ listIncreaseGiftChartBar, activeTab, setActiveTab, listIncreaseGiftChart, handleOpenModal }: ContentChartProps) => {
  const navigation = useNavigate();
  const tabs = [
    { id: 1, title: "روزانه", label: "daly" },
    { id: 2, title: "هفتگی", label: "weekly" },
    { id: 3, title: "ماهانه", label: "monthly" },
  ];

  const categories = listIncreaseGiftChart?.map((item: any) =>
    item?.date ? moment(item.date).locale("fa").format("dddd") : "تاریخ نامعتبر"
  );

  const series = [
    {
      name: "",
      data: listIncreaseGiftChart?.map((item: any) => item.transactions_count),
    },
  ];

  const maxItem = listIncreaseGiftChart?.reduce((prev: any, current: any) => {
    return current.balance > prev.balance ? current : prev;
  }, listIncreaseGiftChart[0]);

  return (
    <StackKit sx={{ width: "100%",padding:2 }}>
      <Grid container alignItems={'center'} spacing={2}>
        <Grid size={{ xs: 12, md: 8 }} sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", paddingRight: { xs: "0", md: "14px" }, paddingTop: "14px", alignItems: "start"}}>
          <TypoGraphyKit variant="h6">نمودار تراکنش های هدیه</TypoGraphyKit>
          <StackKit direction={"row"} flexWrap="wrap" justifyContent={"start"} sx={{bgcolor: "#5A5A5A",padding: "10px",borderRadius: "8px",gap: { xs: 1, sm: 2 }}} alignItems={"center"}>
            {tabs.map((tab) => (
              <StackKit
                key={tab.id}
                onClick={() => { setActiveTab(tab); }}
                sx={{
                  padding: "6px 16px",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  borderRadius: "8px",
                  bgcolor: activeTab === tab.label ? "#434343" : "transparent",
                  color: activeTab === tab.label ? "#fff" : "#B3B3B3",
                  border: activeTab === tab.label ? "1px solid #434343" : "1px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: activeTab === tab.label ? "#434343" : "#666666",
                  },
                }}
              >
                <TypoGraphyKit variant="body2" sx={{ color: "inherit" }}>
                  {tab.title}
                </TypoGraphyKit>
              </StackKit>
            ))}
          </StackKit>
          <StackKit sx={{ width: "100%", padding: { xs: "0 10px", md: "0 30px" } }}>
            <ColumnChart categories={categories} series={series} barWidth="50%" colors={["#800101"]} />
          </StackKit>
          <StackKit direction="row" flexWrap="wrap" sx={{ width: "100%", marginBlock: "20px" }} justifyContent={"space-between"}>
            <TypoGraphyKit variant="body1">
              مجموع بیشترین تراکنش های هدیه ( {toPersianNumber(7)} روز گذشته):
            </TypoGraphyKit>
            <StackKit sx={{ width: { xs: "100%", sm: "40%" } }} direction={"row"} justifyContent={"space-between"}>
              {maxItem ? (
                <TypoGraphyKit variant="body1">
                  {maxItem && numberSeparator(maxItem?.balance)}ریال
                </TypoGraphyKit>
              ) : (
                <TypoGraphyKit variant="body1" sx={{ color: "#FFFFFF" }}>
                  اطلاعاتی موجود نیست
                </TypoGraphyKit>
              )}
              {maxItem ? (
                <TypoGraphyKit variant="body1" sx={{ color: "#FFFFFF" }}>
                  {maxItem?.day || "-"}{" "}
                  {maxItem?.date ? toPersianNumber(maxItem.date) : "-"}
                </TypoGraphyKit>
              ) : (
                <TypoGraphyKit variant="body1" sx={{ color: "#FFFFFF" }}>
                  اطلاعاتی موجود نیست
                </TypoGraphyKit>
              )}
            </StackKit>
          </StackKit>
          <StackKit direction="row" flexWrap="wrap" sx={{ width: "100%" }} justifyContent={"space-between"}>
            <TypoGraphyKit variant="body1">
              بیشترین تراکنش ثبت شده توسط کاربر ( {toPersianNumber(7)} روز گذشته):
            </TypoGraphyKit>
            <StackKit sx={{ width: { xs: "100%", sm: "40%" } }} direction={"row"} justifyContent={"space-between"}>
              {maxItem ? (
                <TypoGraphyKit variant="body1">
                  {maxItem && numberSeparator(maxItem?.balance)}ریال
                </TypoGraphyKit>
              ) : (
                <TypoGraphyKit variant="body1" sx={{ color: "#FFFFFF" }}>
                  اطلاعاتی موجود نیست
                </TypoGraphyKit>
              )}
              {maxItem ? (
                <TypoGraphyKit variant="body1" sx={{ color: "#FFFFFF" }}>
                  {maxItem?.day || "-"}{" "}
                  {maxItem?.date ? toPersianNumber(maxItem.date) : "-"}
                </TypoGraphyKit>
              ) : (
                <TypoGraphyKit variant="body1" sx={{ color: "#FFFFFF" }}>
                  اطلاعاتی موجود نیست
                </TypoGraphyKit>
              )}
            </StackKit>
          </StackKit>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex", flexDirection: "column", borderRight: { xs: "none", md: "3px solid #757575" } }}>
          <StackKit direction={"column"} sx={{ padding: " 10px", borderBottom: "3px solid #757575" }}>
            <StackKit direction={{sm:'row',xs:'column'}} sx={{ width: "100%"}} justifyContent={"space-between"}>
              <StackKit direction={"column"}>
                <TypoGraphyKit variant="body1">موجودی مصرف شده</TypoGraphyKit>
                <StackKit direction={"row"} sx={{ bgcolor: "#1E1E1E", borderRadius: "10px", padding: "10px 5px", minWidth: "167px", }} justifyContent={"space-between"} alignItems={"center"}>
                  <BoxKit component={"div"} sx={{ width: "16px", height: "16px", borderRadius: "50%", bgcolor: "#000000", display: "block", marginLeft: "20px", }} />
                  <TypoGraphyKit variant="body1">
                    {" "}{numberSeparator(listIncreaseGiftChartBar?.balance_used)}{" "}ریال
                  </TypoGraphyKit>
                </StackKit>
              </StackKit>
              <StackKit direction={"column"} sx={{ marginLeft: "0" }}>
                <TypoGraphyKit variant="body1">موجودی باقیمانده</TypoGraphyKit>
                <StackKit direction={"row"} sx={{ bgcolor: "#1E1E1E", borderRadius: "10px", padding: "10px 5px", minWidth: "167px", }} justifyContent={"space-between"} alignItems={"center"}>
                  <BoxKit component={"div"} sx={{ width: "16px", height: "16px", borderRadius: "50%", bgcolor: "#800101", display: "block", marginLeft: "20px", }} />
                  <TypoGraphyKit variant="body1">
                    {numberSeparator(listIncreaseGiftChartBar?.remain_balance)}{" "}ریال
                  </TypoGraphyKit>
                </StackKit>
              </StackKit>
            </StackKit>
            <StackKit direction={"column-reverse"} alignItems={"center"}>
              <StackKit direction={"row"} sx={{ alignItems: "end", width: "100%" }} justifyContent={"start"}>
                <CustomButton sx={{ bgcolor: "#800101", gap: 3, width: "fit-content", height: "41px", }} onClick={handleOpenModal}>
                  <TypoGraphyKit variant="body1">
                    افزایش موجودی هدیه پذیرنده
                  </TypoGraphyKit>
                  <AddIcon />
                </CustomButton>
              </StackKit>
              <StackKit sx={{ width: "60%", position: "relative", justifyContent: "end", flexDirection: "row", }}>
                <RadialBarChart value={listIncreaseGiftChartBar?.balance_used || 0} label="نمودار موجودی هدیه" color="#A90000" />
              </StackKit>
            </StackKit>
          </StackKit>
          <StackKit direction={"column"} sx={{ padding: " 10px", borderBottom: "3px solid #757575" }}>
            <StackKit direction={{sm:'row',xs:'column'}} sx={{ width: "100%"}} justifyContent={"space-between"}>
              <StackKit direction={"column"}>
                <TypoGraphyKit variant="body1">موجودی مصرف شده</TypoGraphyKit>
                <StackKit direction={"row"} sx={{ bgcolor: "#1E1E1E", borderRadius: "10px", padding: "10px 5px", minWidth: "167px", }} justifyContent={"space-between"} alignItems={"center"}>
                  <BoxKit component={"div"} sx={{ width: "16px", height: "16px", borderRadius: "50%", bgcolor: "#000000", display: "block", marginLeft: "20px", }} />
                  <TypoGraphyKit variant="body1">
                    {numberSeparator(listIncreaseGiftChartBar?.findGiftSpend)}{" "}ریال
                  </TypoGraphyKit>
                </StackKit>
              </StackKit>
              <StackKit direction={"column"}>
                <TypoGraphyKit variant="body1">موجودی باقیمانده</TypoGraphyKit>
                <StackKit direction={"row"} sx={{ bgcolor: "#1E1E1E", borderRadius: "10px", padding: "10px 15px", minWidth: "167px", }} justifyContent={"space-between"} alignItems={"center"}>
                  <BoxKit component={"div"} sx={{ width: "16px", height: "16px", borderRadius: "50%", bgcolor: "#800101", display: "block", marginLeft: "20px", }} />
                  <TypoGraphyKit variant="body1">
                    {numberSeparator(listIncreaseGiftChartBar?.remain_gift_spend)}{" "}ریال
                  </TypoGraphyKit>
                </StackKit>
              </StackKit>
            </StackKit>
            <StackKit direction={"column-reverse"} alignItems={"center"}>
              <StackKit direction={"row"} sx={{ alignItems: "end", width: "100%" }} justifyContent={"start"}>
                <CustomButton sx={{ bgcolor: "#800101", gap: 3, width: "fit-content", height: "41px", }} onClick={() => navigation("/customer-my-organization")}>
                  <TypoGraphyKit variant="body1">
                    افزایش هدیه کاربران
                  </TypoGraphyKit>
                  <AddIcon />
                </CustomButton>
              </StackKit>
              <StackKit sx={{ width: "60%", position: "relative", justifyContent: "end", flexDirection: "row", }}>
                <RadialBarChart value={listIncreaseGiftChartBar?.findGiftSpend || 0} label="نمودار هدیه های اهدا شده" color="#A90000" />
              </StackKit>
            </StackKit>
          </StackKit>
        </Grid>
      </Grid>
    </StackKit>
  );
};

export default ContentChart;
